import './index.css'

import clsx from 'clsx'
import React from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { Link, NavigateFunction, useNavigate } from 'react-router-dom'

import { updatePricingForm } from '@disco/state-management/actions/FormActions'
import type { PricingFormState } from '@disco/state-management/reducers/forms/pricing'
import { isDeviceTypePhone } from '@disco/state-management/selectors/app'
import {
  ARTIST_ID,
  ENTERPRISE_ID,
  PLUS_ID,
  PRO_ID,
} from '@disco/utils/featuresAccessLevel'
import type { BillingRootPath } from '@disco/utils/pricing'
import { getDefaultPlanQuotas } from '@disco/utils/pricing'

import type { AccountPlan } from 'types/business2020'
import type { User } from 'types/user'

import Button from 'components/ui/button'
import CustomDropdown from 'components/ui/dropdowns/custom-dropdown'
import List from 'components/ui/list'
import RadioButton from 'components/ui/radio/radio-button/check'
import Tooltip from 'components/ui/tooltip'

import { RootState } from 'store/configureStore'

import ChosenBadge from './chosen-badge'
import Price from './price'

type PropsFromRedux = ConnectedProps<typeof connector>

type Props = PropsFromRedux & {
  deprecatedPlan: boolean
  isChosen: boolean
  maxFilesCurrentPlan: number
  maxUsersCurrentPlan: number
  plan: AccountPlan
  user: User
  rootPath: BillingRootPath
  isPlanChange: boolean
  usersLimitsCurrentPlan: number
  tracksLimitsCurrentPlan: number
  navigate: NavigateFunction
}
type State = {
  filesRangeValue: number | null | undefined
  usersRangeValue: number | null | undefined
}
type getCostProps = [number | null | undefined, number | null | undefined]

const mapStateToProps = (state: RootState) => ({
  pricingForm: state.forms.pricingForm,
  period: state.forms.pricingForm.paymentPeriod,
  isPhone: isDeviceTypePhone(state),
})

const mapDispatchToProps = {
  updatePricingForm,
}

const connector = connect(mapStateToProps, mapDispatchToProps)

class PricingPlan extends React.Component<Props, State> {
  state: State = {
    filesRangeValue: null,
    usersRangeValue: null,
  }

  componentDidMount() {
    const {
      plan,
      updatePricingForm,
      pricingForm,
      usersLimitsCurrentPlan,
      tracksLimitsCurrentPlan,
    } = this.props

    if (
      pricingForm.plan?.handle === plan.handle &&
      pricingForm.componentsQuantity.tracks &&
      pricingForm.componentsQuantity.users
    ) {
      this.setState({
        usersRangeValue: pricingForm.componentsQuantity.users,
        filesRangeValue: pricingForm.componentsQuantity.tracks,
      })
      return
    }

    // TODO: refactor default quota calculations based on user.business.stats and steps
    const usersFallback = getDefaultPlanQuotas(plan.handle).users
    const userValue = Math.min(
      usersLimitsCurrentPlan !== Infinity
        ? usersLimitsCurrentPlan
        : usersFallback,
      plan.users.max ?? usersFallback,
    )
    const tracksFallback = getDefaultPlanQuotas(plan.handle).files
    const tracksValue = Math.min(
      tracksLimitsCurrentPlan !== Infinity
        ? tracksLimitsCurrentPlan
        : tracksFallback,
      plan.files.max ?? getDefaultPlanQuotas(plan.handle).files,
    )
    this.setState({
      usersRangeValue: userValue,
      filesRangeValue: tracksValue,
    })

    if (!pricingForm.plan && (plan.recommended || plan.autoSelect)) {
      updatePricingForm({
        plan,
        componentsQuantity: {
          ...pricingForm.componentsQuantity,
          tracks: tracksValue,
          users: userValue,
        },
      })
    }
  }

  static getDerivedStateFromProps(props, state) {
    const { plan, pricingForm } = props
    const { usersRangeValue, filesRangeValue } = state

    if (
      pricingForm.plan?.handle === plan.handle &&
      (pricingForm.componentsQuantity.tracks !== filesRangeValue ||
        pricingForm.componentsQuantity.users !== usersRangeValue)
    ) {
      return {
        usersRangeValue: pricingForm.componentsQuantity.users,
        filesRangeValue: pricingForm.componentsQuantity.tracks,
      }
    }

    return null
  }

  isUpgradeCurrentPlan(): number {
    const { maxUsersCurrentPlan, maxFilesCurrentPlan } = this.props
    const { usersRangeValue, filesRangeValue } = this.state
    const userCondition = Number(maxUsersCurrentPlan) !== usersRangeValue
    const filesCondition = Number(maxFilesCurrentPlan) !== filesRangeValue

    if (
      (userCondition || filesCondition) &&
      usersRangeValue &&
      filesRangeValue
    ) {
      const isUpgradeCurrentPlan =
        maxUsersCurrentPlan < usersRangeValue ||
        maxFilesCurrentPlan < filesRangeValue
      return isUpgradeCurrentPlan ? 1 : -1
    }

    return 0
  }

  getCost(priceList: getCostProps[]): number | null | undefined {
    const { plan } = this.props
    if (!plan.cost) return null
    let price = priceList.reduce(
      (accumulator, [value, cost]) =>
        accumulator + Number(value) * Number(cost),
      0,
    )
    const planCost = this.getCostWithPeriod(plan)

    if (isNaN(price)) {
      price = planCost
    } else if (planCost) {
      price += planCost
    }

    if (price) {
      price = Number(`${price}`)
    }

    return price
  }

  _showingFractions(
    number: number | null | undefined,
  ): string | (number | null | undefined) {
    if (!Number.isInteger(number) && number) {
      return String(number.toFixed(2))
    }

    return number
  }

  getCostWithPeriod(item = {}) {
    const { period } = this.props
    return item[period === 'annual' ? 'annualCost' : 'cost']
  }

  getPlanPrice() {
    const { usersRangeValue, filesRangeValue } = this.state
    let { plan } = this.props
    const costUser = this.getCostWithPeriod(plan.users)
    const costFiles = this.getCostWithPeriod(plan.files)
    const correctNumberOfUsers =
      (usersRangeValue || 1) - getDefaultPlanQuotas(plan.handle).users
    const correctNumberOfFiles =
      (filesRangeValue || 1000) - getDefaultPlanQuotas(plan.handle).files
    let price = this.getCost([
      [correctNumberOfUsers, costUser],
      [correctNumberOfFiles, costFiles],
    ])
    return {
      price,
    }
  }

  _renderPricingContent() {
    const { handle } = this.props.plan

    if (handle === ARTIST_ID) {
      return (
        <p className="PricingPlan_Description">2 users and unlimited tracks</p>
      )
    }

    if (handle !== ENTERPRISE_ID) {
      return (
        <>
          <div className="PricingPlan_PricingDropdownContainer">
            <span className="PricingPlan_PricingDropdownTitle">
              Select Users
            </span>
            {this._renderPricingOptions('users')}
          </div>
          <div className="PricingPlan_PricingDropdownContainer">
            <span className="PricingPlan_PricingDropdownTitle">
              Select Tracks
            </span>
            {this._renderPricingOptions('files')}
          </div>
        </>
      )
    }
  }

  _renderPricingOptions(type: 'users' | 'files') {
    const {
      user,
      plan,
      updatePricingForm,
      pricingForm,
      rootPath,
      usersLimitsCurrentPlan,
      tracksLimitsCurrentPlan,
    } = this.props
    const data = plan[type]
    const isUsers = type === 'users'
    const rangeValue = this.state[`${type}RangeValue`]
    const isCurrentPlan =
      user.business.private_metadata.plan?.id &&
      user.business.private_metadata.plan?.id === plan.handle
    let startValue, maxValue

    if (isCurrentPlan) {
      startValue = isUsers ? usersLimitsCurrentPlan : tracksLimitsCurrentPlan
      maxValue = isUsers ? usersLimitsCurrentPlan : tracksLimitsCurrentPlan
    } else {
      startValue = data?.defaultCount || 0
      maxValue = data?.max || 0
    }

    const options: Array<{ value: number; label: string }> = []

    for (let i = startValue; i <= maxValue; ) {
      if (isUsers) {
        options.push({
          value: i,
          label: String(i),
        })
        i++
      } else {
        const stepFiles = i >= 10000 ? 5000 : 1000
        const valueFiles = Math.ceil(i / stepFiles) * stepFiles
        options.push({
          value: valueFiles,
          label: String(valueFiles),
        })
        i += stepFiles
      }
    }

    return (
      <div className="PricingPlan_PricingDropdown">
        <CustomDropdown
          shortMenu
          value={{
            value: rangeValue,
            label: rangeValue,
          }}
          onChange={option => {
            this.setState({
              [`${type}RangeValue`]: option.value,
            } as any)

            if (
              pricingForm.componentsQuantity[isUsers ? 'users' : 'tracks'] !==
              option.value
            ) {
              if (
                pricingForm.plan &&
                pricingForm.plan[isUsers ? 'users' : 'files']?.max ===
                  option.value
              ) {
                const tracks = isUsers
                  ? pricingForm.componentsQuantity.tracks
                  : option.value
                const users = isUsers
                  ? option.value
                  : pricingForm.componentsQuantity.users

                if (users && tracks) {
                  this.props.navigate(
                    `${rootPath}/need-more-users-or-tracks-modal?users=${users}&tracks=${tracks}`,
                  )
                }
              }

              if (pricingForm.plan?.handle === plan.handle) {
                updatePricingForm({
                  plan,
                  componentsQuantity: {
                    ...pricingForm.componentsQuantity,
                    [isUsers ? 'users' : 'tracks']: option.value,
                  },
                })
              }
            }
          }}
          options={options}
        />
      </div>
    )
  }

  render() {
    let {
      deprecatedPlan,
      isChosen,
      plan,
      maxFilesCurrentPlan,
      maxUsersCurrentPlan,
      pricingForm,
      updatePricingForm,
      rootPath,
      isPhone,
      isPlanChange,
      user,
    } = this.props
    const { usersRangeValue, filesRangeValue } = this.state
    const { name: planTitle, topFeatures, handle, featuresTitle } = plan
    let usersQuantity = usersRangeValue || 0
    let tracksQuantity = filesRangeValue || 0

    if (!deprecatedPlan && isChosen) {
      if (usersRangeValue) {
        usersQuantity -= maxUsersCurrentPlan
      }

      if (filesRangeValue) {
        tracksQuantity -= maxFilesCurrentPlan
      }
    }

    if (!isChosen) {
      if (usersQuantity > 0) {
        //The cost of the plan by default includes some amount of users
        usersQuantity -= getDefaultPlanQuotas(handle).users
      }

      if (tracksQuantity > 0) {
        //The cost of the plan by default includes some amount of tracks
        tracksQuantity = Math.max(
          tracksQuantity - getDefaultPlanQuotas(handle).files,
          0,
        )
      }
    }

    const focus =
      pricingForm.plan?.handle && pricingForm.plan?.handle === handle
    const currentPlan =
      user.business.private_metadata.plan?.id &&
      user.business.private_metadata.plan?.id === handle
    const currentPlanHandle = user.business.private_metadata.plan?.id
    const pricingPlanTitle = (
      <div className="PricingPlan_TitleWrapper">
        <div className="PricingPlan_TitleContainer">
          {!isPlanChange && (
            <div className="PricingPlan_RadioButton">
              <RadioButton checked={Boolean(focus)} />
            </div>
          )}

          <span className="PricingPlan_Title">{planTitle}</span>
        </div>
        <Price
          currencySymbol={`$`}
          price={<>{this._showingFractions(this.getPlanPrice().price)}</>}
          period="/mo"
        />
      </div>
    )
    const pricingPlanContent = (
      <div className="PricingPlan_ContentWrapper">
        <div className="PricingPlan_ContentContainer">
          {this._renderPricingContent()}
        </div>
      </div>
    )
    const pricingPlanList = (
      <List
        className={clsx('PricingPlan_ListFeatures ', {
          'PricingPlan_ListFeatures--Checked': handle === ARTIST_ID,
        })}
      >
        {featuresTitle && (
          <List.Item className="PricingPlan_FeaturesTitle">
            {featuresTitle}
          </List.Item>
        )}
        {topFeatures.map((item, index) => (
          <List.Item
            key={index}
            className={clsx(
              'PricingPlan_ItemFeature',
              {
                'PricingPlan_ItemFeature--Checked': handle === ARTIST_ID,
              },
              {
                'PricingPlan_ListFeatures--TopFeatures': handle !== ARTIST_ID,
              },
            )}
          >
            {item}
          </List.Item>
        ))}
      </List>
    )
    let pricingPlanDetails = !isPhone ? (
      <Link
        className="PricingPlan_PlanDetails"
        to={`${rootPath}/feature-table`}
        state={{
          returnTo: `${rootPath}`,
        }}
      >
        See more Plan details
      </Link>
    ) : null

    if (pricingPlanDetails && handle === ARTIST_ID) {
      pricingPlanDetails = (
        <a
          className="PricingPlan_PlanDetails"
          target="_blank"
          rel="noopener noreferrer"
          href="https://www.disco.ac/artist-plan-comparison"
        >
          See more Plan details
        </a>
      )
    }

    const handlePlanClick = (
      event?: React.MouseEvent<HTMLDivElement>,
      isPlanChangeParam?: boolean,
    ) => {
      if (
        (event?.target as HTMLElement)?.classList?.contains(
          'PricingPlan_PlanDetails',
        ) ||
        isPlanChangeParam
      ) {
        return
      }

      if (pricingForm.plan?.handle !== handle) {
        updatePricingForm({
          plan,
          componentsQuantity: {
            ...pricingForm.componentsQuantity,
            tracks: this.state.filesRangeValue,
            users: this.state.usersRangeValue,
          },
        })
      }
    }

    const handleKeyDown = event => {
      if (event.key === 'Enter' || event.key === ' ') {
        handlePlanClick()
      }
    }

    const ctaText = () => {
      if (currentPlan) {
        return `Your current plan`
      }

      if (currentPlanHandle === PRO_ID && handle === PLUS_ID) {
        return `Downgrade to ${planTitle}`
      }

      if (!currentPlan) {
        return `Upgrade to ${planTitle}`
      }
    }

    const isDowngradeAllowed =
      (usersRangeValue != null && maxUsersCurrentPlan < usersRangeValue) ||
      (filesRangeValue != null && maxFilesCurrentPlan < filesRangeValue)
    const pricingFormToQueryString = {
      handle: handle,
      period: this.props.period,
      users: this.state.usersRangeValue?.toString() || '',
      tracks: this.state.filesRangeValue?.toString() || '',
      watermarking:
        pricingForm.componentsQuantity.watermarking?.toString() || '',
      discovery_suite:
        pricingForm.componentsQuantity.discoverySuite?.toString() || '',
    }
    const params = new URLSearchParams(pricingFormToQueryString)
    const queryStringPricingForm = params.toString()
    return (
      <div
        role="button"
        tabIndex={0}
        onKeyDown={handleKeyDown}
        className={clsx('PricingPlan', {
          'PricingPlan--Wide': handle === ARTIST_ID,
        })}
        onClick={event => handlePlanClick(event, isPlanChange)}
      >
        <div
          className="PricingPlan_UpperCont"
          style={focus ? { ...plan.chosenColor.boxShadow } : {}}
        >
          <ChosenBadge plan={plan} isPlanChange={isPlanChange}>
            {handle === ARTIST_ID ? (
              <>
                <div className="PricingPlan_LeftContent">
                  {pricingPlanTitle}
                  {pricingPlanContent}
                </div>
                <div className="PricingPlan_RightContent">
                  <span className="PricingPlan_FeaturesTitle">
                    Top features
                  </span>
                  {pricingPlanList}
                  {pricingPlanDetails}
                </div>
              </>
            ) : (
              <>
                {pricingPlanTitle}
                {pricingPlanContent}
                <div className="PricingPlan_FeaturesWrapper">
                  {pricingPlanList}
                  {isPlanChange && (
                    <div className={'PricingPlan_ChangePlanButton'}>
                      <Tooltip
                        block
                        content={
                          !isDowngradeAllowed && !currentPlan
                            ? 'Plan downgrade is only available for accounts with 10 users and 2000 tracks or fewer.'
                            : undefined
                        }
                      >
                        <Button
                          appearance="primary"
                          caption={ctaText()}
                          link={`/settings/billing/summary?${queryStringPricingForm}`}
                          disabled={!!currentPlan || !isDowngradeAllowed}
                          fullWidth
                          textCenter
                          onClick={event => handlePlanClick(event, false)}
                        />
                      </Tooltip>
                    </div>
                  )}
                  {pricingPlanDetails}
                </div>
              </>
            )}
          </ChosenBadge>
        </div>
      </div>
    )
  }
}

const ConnectedPricingPlan = connector(PricingPlan)

function PricingPlanWithNavigate(props: any) {
  const navigate = useNavigate()
  return <ConnectedPricingPlan {...props} navigate={navigate} />
}

export default PricingPlanWithNavigate
